import { lazy, Suspense, useEffect } from 'react';

// components - first screen (Home) stays in the main bundle
import { HomeBody } from './';

// components - every other screen loads on first open (own chunk; the service worker precaches them)
const named = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const SearchBody = named(() => import('./search/search-body'), 'SearchBody');
const ExploreBody = named(() => import('./explore/explore-body'), 'ExploreBody');
const TrendingBody = named(() => import('./trending/trending-body'), 'TrendingBody');
const MessageBody = named(() => import('./message/message-body'), 'MessageBody');
const ConversationHead = named(() => import('./message/conversation-view'), 'ConversationHead');
const ConversationView = named(() => import('./message/conversation-view'), 'ConversationView');
const SettingBody = named(() => import('./settings/settings-body'), 'SettingBody');
const ProfileBody = named(() => import('./profile/profile-body'), 'ProfileBody');
const UserListHead = named(() => import('./userlist/userlist-head'), 'UserListHead');
const UserListBody = named(() => import('./userlist/userlist-body'), 'UserListBody');
const LegalBody = named(() => import('./legal/legal-body'), 'LegalBody');
const LoginBody = named(() => import('./unauthenticated/login-body'), 'LoginBody');
const SignUpBody = named(() => import('./unauthenticated/signup-body'), 'SignUpBody');

// Loading dots while a lazy screen's chunk arrives (the top and bottom bars stay).
const body = (el) => <Suspense fallback={<DotLoading variant='pulse' sx={{ height: '100%' }} />}>{el}</Suspense>;
const head = (el) => <Suspense fallback={null}>{el}</Suspense>;

// components - gateway pages
import {
    MaintenanceBody,
    UnauthorizedBody,
    TopContent,
    BottomContent,
    setNavigation,
    createHomeProp,
    createLegalProp,
    LoginRequiredBody,
    isGuest,
    takeReturnContainers,
    addNavigation,
    createProfileProp,
    parseDeeplink,
} from './';

// api
import { UserByUsername } from '../api';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ContainerMain, DotLoading } from '@goldblack/client-comps';

// Login-only screens show a login prompt to guests instead of their content. The tab bar stays,
// so a guest can move on to other tabs. Own-profile tab = 'profile' without a target.
const LOGIN_SCREENS = new Set(['home', 'messages', 'settings', 'conversation']);
const needsLogin = (props) =>
    isGuest() && (LOGIN_SCREENS.has(props.path) || (props.path === 'profile' && !props.target));

// A profile deep link needs a username lookup, so it opens on top of the base screen once resolved.
async function openUserDeeplink(link) {
    const user = await UserByUsername(link.username);
    if (!user) {
        storage.transience.setState('internal.warning', 'This user could not be found.');
        return;
    }
    addNavigation(createProfileProp(user, link.postId));
}

// The first screen is up: index.html then starts the deferred loads (ads, fonts, Sentry). The screen's content still
// paints a few renders after the containers are set, so wait 1 s (signalling right away left mobile Lighthouse LCP at
// 4.1 s instead of 2.7 s). Repeat calls are harmless (window.appReady resolves once).
function signalAppReady() {
    setTimeout(() => window.dispatchEvent(new Event('app-ready')), 1000);
}

// Authenticated routes (login required)
function AuthenticatedRoutes() {
    const { main } = storage.transience.getValue();

    useEffect(() => {
        // Everyone lands on Home. For a guest it shows the login prompt; the tab bar still opens
        // Explore / Search / Trending.
        const base = createHomeProp();

        const deeplink = storage.transience.getValue('path.deeplink');
        if (deeplink?.length > 0) storage.transience.setState('path.deeplink', null);
        const link = parseDeeplink(deeplink);
        let homeProp = base;

        // Back from the login page (logged in or "continue browsing"): reopen the screen
        // the guest was on when an action asked for login. With a pending legal notice it
        // becomes the screen shown after the notice (the stack holds a single screen).
        const returnContainers = takeReturnContainers(base);
        if (returnContainers) homeProp = returnContainers[returnContainers.length - 1];

        if (main.notice.length > 0) {
            storage.transience.setState({
                'main.containers': [createLegalProp(main.notice[0])],
                'main.stack': homeProp,
            });
        } else if (returnContainers) {
            storage.transience.setState('main.containers', returnContainers);
        } else {
            setNavigation(homeProp);
            if (link) openUserDeeplink(link);
        }
    }, []);

    const hasScreen = main.containers.length > 0;
    useEffect(() => {
        if (hasScreen) signalAppReady();
    }, [hasScreen]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (main.notice.length > 0 && main.stack) {
            setNavigation(createLegalProp(main.notice[0]));
        }
    }, [main.notice]);

    const lastIndex = Math.max(0, main.containers.length - 1);
    return (
        <>
            {main.containers.map((props, index) => {
                const hidden = index !== lastIndex;
                return props.path === 'home' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={
                            needsLogin(props) ? (
                                <LoginRequiredBody path={props.path} />
                            ) : (
                                <HomeBody {...props} hidden={hidden} />
                            )
                        }
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'search' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={body(<SearchBody {...props} />)}
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'explore' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={body(<ExploreBody {...props} />)}
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'trending' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={body(<TrendingBody {...props} />)}
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'messages' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={
                            needsLogin(props) ? (
                                <LoginRequiredBody path={props.path} />
                            ) : (
                                body(<MessageBody {...props} />)
                            )
                        }
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'settings' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={
                            needsLogin(props) ? (
                                <LoginRequiredBody path={props.path} />
                            ) : (
                                body(<SettingBody {...props} />)
                            )
                        }
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'legal' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={body(<LegalBody {...props} />)}
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : // depth +1
                props.path === 'profile' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={<TopContent {...props} />}
                        bodyContent={
                            needsLogin(props) ? (
                                <LoginRequiredBody path={props.path} />
                            ) : (
                                body(<ProfileBody {...props} hidden={hidden} />)
                            )
                        }
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'userlist' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={head(<UserListHead {...props} />)}
                        bodyContent={body(<UserListBody {...props} />)}
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : props.path === 'conversation' ? (
                    <ContainerMain
                        key={index}
                        hidden={hidden}
                        top
                        bottom
                        border
                        width='xs'
                        config={props.config}
                        topContent={head(<ConversationHead {...props} />)}
                        bodyContent={
                            needsLogin(props) ? (
                                <LoginRequiredBody path={props.path} />
                            ) : (
                                body(<ConversationView {...props} />)
                            )
                        }
                        bottomContent={<BottomContent {...props} />}
                    />
                ) : (
                    <></>
                );
            })}
        </>
    );
}

function UnauthenticatedRoutes({ path }) {
    const localConfig = storage.localStorage.getValue('config');

    useEffect(signalAppReady, []);

    return path === 'login' ? (
        <ContainerMain width='xs' config={localConfig.data} bodyContent={body(<LoginBody />)} />
    ) : path === 'signup' ? (
        <ContainerMain width='xs' config={localConfig.data} bodyContent={body(<SignUpBody />)} />
    ) : path === 'unauthorized' ? (
        <ContainerMain width='xs' config={localConfig.data} bodyContent={<UnauthorizedBody />} />
    ) : path === 'maintenance' ? (
        <ContainerMain width='xs' config={localConfig.data} bodyContent={<MaintenanceBody />} />
    ) : (
        <></>
    );
}

// Gateway - handles all routing
function Gateway() {
    const path = storage.transience.getValue('path.gateway');

    // Authenticated → stack-based routing
    if (path === 'main') {
        return <AuthenticatedRoutes />;
    }

    return <UnauthenticatedRoutes path={path} />;
}

export { Gateway };
